import { useCallback } from "react";

import { Modal, Col, Typography, Row, Tooltip } from "antd";
import { clsx } from "clsx";
import { useNavigate, useSearchParams } from "react-router-dom";

import { BADGE_STATUS, RVButton, RVStatusBadge, RVTimeDisplay } from "@regal-voice/ui";
import { useOrchestrationPathing } from "Pages/orchestration/useOrchestrationPathing";
import { JourneyVersion, JourneyStatus } from "Types/Journey";

import { useRestoreJourneyVersion } from "../../hooks/useRestoreJourneyVersion";

import styles from "./JourneyVersionItem.module.scss";

type JourneyVersionItemProps = {
    journeyVersionData: JourneyVersion;
    journeyId: string;
    currentVersion?: string;
    onClose: () => void;
};

export function JourneyVersionItem({
    journeyVersionData,
    journeyId,
    currentVersion,
    onClose,
}: JourneyVersionItemProps): JSX.Element {
    const { version, createdAt, createdBy, isActive, state, triggerType } = journeyVersionData;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { restoreVersion, loading } = useRestoreJourneyVersion();
    const { journeysBasePath } = useOrchestrationPathing();

    const versionString = version.toString();
    const isScheduledJourney = triggerType === "scheduled";

    const handleClick = useCallback(() => {
        if (currentVersion === versionString) {
            return;
        }
        // Navigate to the journey with the version query parameter
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set("version", versionString);
        navigate(`${journeysBasePath}/${journeyId}?${newSearchParams.toString()}`);
    }, [journeyId, currentVersion, navigate, versionString, searchParams, journeysBasePath]);

    const isSelected = currentVersion === versionString;

    const handleVersionRestore = useCallback(async () => {
        const success = await restoreVersion(journeyId, versionString);
        // Close the versions panel only after a successful restore
        if (success) {
            onClose();
        }
    }, [restoreVersion, journeyId, versionString, onClose]);

    const handleRestore = useCallback(() => {
        Modal.confirm({
            title: "Restore this version?",
            content: "This will restore the version and create a new version.",
            okText: "Restore",
            cancelText: "Cancel",
            onOk: handleVersionRestore,
        });
    }, [handleVersionRestore]);

    // Determine badge status based on journey state
    const badgeStatus = state === JourneyStatus.LIVE ? BADGE_STATUS.ACTIVE : BADGE_STATUS.PENDING;
    const badgeContent = state === JourneyStatus.LIVE ? "Live" : "Draft";

    return (
        <div
            className={clsx(styles.versionItem, isSelected && styles.isSelected)}
            data-testid={`version-item-${version}`}
            onClick={handleClick}
        >
            <Col>
                <div className={styles.rightContainer}>
                    <Typography.Text className={styles.version}>Version {version}</Typography.Text>
                    <div className={styles.statusBadge}>
                        <RVStatusBadge status={badgeStatus} content={badgeContent} />
                    </div>
                </div>
                <Row className={styles.createdAtRow}>
                    <Col>
                        <Typography.Text className={styles.createdAt}>
                            <RVTimeDisplay time={createdAt} />
                        </Typography.Text>
                    </Col>
                    <Col>
                        <Tooltip title={createdBy && createdBy.length > 20 ? createdBy : null} placement="bottom">
                            <Typography.Text className={styles.createdBy}>by {createdBy || "Unknown"}</Typography.Text>
                        </Tooltip>
                    </Col>
                </Row>
            </Col>
            <div className={styles.leftContainer}>
                {isActive && <div className={styles.versionStatus}>Current Version</div>}
                {!isActive && isSelected && (
                    <Tooltip
                        title={isScheduledJourney ? "Restore not available for scheduled journeys." : null}
                        placement="left"
                    >
                        <span>
                            <RVButton loading={loading} onClick={handleRestore} disabled={isScheduledJourney}>
                                Restore
                            </RVButton>
                        </span>
                    </Tooltip>
                )}
            </div>
        </div>
    );
}
